/**
 * 予約商品と即時発送商品のカート混在を防ぐ
 *
 * 発送タイミング（コレクション `_cart_group` で判定）
 *   - pre-order … pre-order-items（予約・入荷後発送）
 *   - regular   … regular-items（即時発送系）
 */
;(function () {
  const CART_GROUP = {
    PRE_ORDER: 'pre-order',
    REGULAR: 'regular',
  }

  const CART_MIX_MESSAGE_HTML =
    'カートに「予約商品」と「即時発送商品」が同時に入っているため、ご購入いただけません。<br/>' +
    'お手数ですが、どちらか一方をカートから削除してご注文いただくか、先にどちらか一方の決済を完了させてから、改めて残りの商品をご注文ください。'

  const GROUP_LABELS = {
    [CART_GROUP.PRE_ORDER]: '予約商品',
    [CART_GROUP.REGULAR]: '即時発送商品',
  }

  const ALERT_CLASSES =
    'cart-mix-guard__alert px-5 py-4 mb-6 text-sm leading-relaxed text-[#fa8484] bg-[#fff3f3] border border-[#fa8484]'

  const ADD_BUTTON_DISABLED_CLASSES = ['opacity-60', 'cursor-not-allowed']

  const CHECKOUT_SELECTORS = [
    '#checkout',
    'button[name="checkout"]',
    '.cart__checkout-button',
  ]

  const PRODUCT_FORM_SELECTORS =
    'form[data-type="add-to-cart-form"], form[data-cart-group]'

  function getPropertyValue(properties, key) {
    if (!properties) return ''

    if (typeof properties === 'object' && !Array.isArray(properties)) {
      return (properties[key] ?? '').toString()
    }

    if (Array.isArray(properties)) {
      const match = properties.find((property) => property.first === key || property[0] === key)
      if (!match) return ''
      return (match.last ?? match[1] ?? '').toString()
    }

    return ''
  }

  function getLineItemGroup(lineItem) {
    const fromProperty = getPropertyValue(lineItem.properties, '_cart_group').trim()
    if (fromProperty) return fromProperty

    const fromDataset = lineItem.dataset?.cartGroup?.trim()
    if (fromDataset) return fromDataset

    return ''
  }

  function getGroupsFromCart(cart) {
    const groups = new Set()

    ;(cart.items || []).forEach((item) => {
      const group = getLineItemGroup(item)
      if (group) groups.add(group)
    })

    return groups
  }

  function isMixedCart(cart) {
    const groups = getGroupsFromCart(cart)
    return groups.has(CART_GROUP.PRE_ORDER) && groups.has(CART_GROUP.REGULAR)
  }

  function wouldMix(cart, incomingGroup) {
    if (!incomingGroup) return false

    const groups = getGroupsFromCart(cart)
    if (groups.size === 0) return false

    if (incomingGroup === CART_GROUP.PRE_ORDER) {
      return groups.has(CART_GROUP.REGULAR)
    }

    if (incomingGroup === CART_GROUP.REGULAR) {
      return groups.has(CART_GROUP.PRE_ORDER)
    }

    return false
  }

  function getCartUrl() {
    return window.routes?.cart_url || '/cart'
  }

  function getOppositeGroup(group) {
    if (group === CART_GROUP.PRE_ORDER) return CART_GROUP.REGULAR
    if (group === CART_GROUP.REGULAR) return CART_GROUP.PRE_ORDER
    return ''
  }

  function getProductFormMixMessage(incomingGroup) {
    const cartGroup = getOppositeGroup(incomingGroup)
    if (!cartGroup) return ''

    const cartLabel = GROUP_LABELS[cartGroup]
    const incomingLabel = GROUP_LABELS[incomingGroup]
    const cartLink = `<a href="${getCartUrl()}" class="underline font-medium">カート</a>`

    return (
      `<strong>この商品は「${incomingLabel}」です。</strong><br/>` +
      `現在カートに入っている「${cartLabel}」と同時に購入することはできません。<br/>` +
      `お手数ですが、一度${cartLink}内の商品を削除して本商品をご注文いただくか、先にカート内の商品の決済を完了させてから、改めて本商品をご注文ください。`
    )
  }

  function markServerDisabledCheckoutButtons() {
    CHECKOUT_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((button) => {
        if (button.disabled && button.dataset.cartMixGuardDisabled !== 'true') {
          button.dataset.checkoutServerDisabled = 'true'
        }
      })
    })
  }

  function markServerDisabledAddButtons() {
    document.querySelectorAll(`${PRODUCT_FORM_SELECTORS} button[type="submit"]`).forEach((button) => {
      if (button.disabled && button.dataset.cartMixGuardAddDisabled !== 'true') {
        button.dataset.addButtonServerDisabled = 'true'
      }
    })
  }

  function setCheckoutButtonsDisabled(disabled) {
    CHECKOUT_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((button) => {
        if (disabled) {
          button.setAttribute('disabled', 'disabled')
          button.setAttribute('aria-disabled', 'true')
          button.dataset.cartMixGuardDisabled = 'true'
          button.classList.add('cart-mix-guard__checkout--disabled')
        } else if (button.dataset.cartMixGuardDisabled === 'true') {
          delete button.dataset.cartMixGuardDisabled
          button.classList.remove('cart-mix-guard__checkout--disabled')

          if (button.dataset.checkoutServerDisabled !== 'true') {
            button.removeAttribute('disabled')
            button.removeAttribute('aria-disabled')
          }
        }
      })
    })
  }

  function getFormSubmitButton(form) {
    return form.querySelector('button[type="submit"]')
  }

  function setFormAddButtonDisabled(form, disabled) {
    const button = getFormSubmitButton(form)
    if (!button) return

    if (disabled) {
      button.disabled = true
      button.setAttribute('aria-disabled', 'true')
      button.dataset.cartMixGuardAddDisabled = 'true'
      ADD_BUTTON_DISABLED_CLASSES.forEach((className) => button.classList.add(className))
      return
    }

    if (button.dataset.cartMixGuardAddDisabled !== 'true') return

    delete button.dataset.cartMixGuardAddDisabled
    ADD_BUTTON_DISABLED_CLASSES.forEach((className) => button.classList.remove(className))

    if (button.dataset.addButtonServerDisabled !== 'true') {
      button.disabled = false
      button.removeAttribute('aria-disabled')
    }
  }

  function showInlineAlert(container, beforeElement, messageHtml) {
    if (!container) return null

    let alert = container.querySelector('[data-cart-mix-inline-alert]')

    if (!alert) {
      alert = document.createElement('div')
      alert.setAttribute('data-cart-mix-inline-alert', 'true')
      alert.setAttribute('role', 'alert')
      alert.className = ALERT_CLASSES

      if (beforeElement) {
        container.insertBefore(alert, beforeElement)
      } else {
        container.prepend(alert)
      }
    }

    alert.innerHTML = messageHtml
    alert.hidden = false
    return alert
  }

  function toggleCartAlert(show) {
    const alerts = document.querySelectorAll('#cart-mix-guard-alert, [data-cart-mix-alert]')

    alerts.forEach((alert) => {
      alert.hidden = !show
      alert.setAttribute('data-cart-mix-blocked', show ? 'true' : 'false')
    })

    if (show && alerts.length === 0) {
      const alert = document.createElement('div')
      alert.id = 'cart-mix-guard-alert'
      alert.setAttribute('data-cart-mix-alert', 'true')
      alert.setAttribute('role', 'alert')
      alert.className = `${ALERT_CLASSES} mb-4`
      alert.innerHTML = CART_MIX_MESSAGE_HTML

      const cartFooter = document.querySelector('#main-cart-footer .cart__footer')
      if (cartFooter) {
        cartFooter.prepend(alert)
        alert.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    } else if (show) {
      alerts[0]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  function applyCartMixState(cart) {
    const blocked = isMixedCart(cart)
    setCheckoutButtonsDisabled(blocked)
    toggleCartAlert(blocked)
    return blocked
  }

  function applyProductFormStates(cart) {
    document.querySelectorAll(PRODUCT_FORM_SELECTORS).forEach((form) => {
      const incomingGroup = getIncomingGroup(form)
      if (!incomingGroup) return

      const anchor =
        form.querySelector('.product-form__buttons') || form.querySelector('button[type="submit"]')
      const shouldBlock = wouldMix(cart, incomingGroup)

      if (shouldBlock) {
        const messageHtml = getProductFormMixMessage(incomingGroup)
        showInlineAlert(form, anchor, messageHtml)
        setFormAddButtonDisabled(form, true)
        return
      }

      setFormAddButtonDisabled(form, false)
      const alert = form.querySelector('[data-cart-mix-inline-alert]')
      if (alert) alert.hidden = true
    })
  }

  async function fetchCart() {
    const response = await fetch(`${getCartUrl()}.js`, { cache: 'no-store' })
    if (!response.ok) throw new Error('Failed to fetch cart')
    return response.json()
  }

  function applyCartMixGuardState(cart) {
    markServerDisabledAddButtons()
    applyCartMixState(cart)
    applyProductFormStates(cart)
  }

  async function refreshCartMixState() {
    try {
      const cart = await fetchCart()
      applyCartMixGuardState(cart)
    } catch (error) {
      console.error('cart-mix-guard: failed to refresh cart state', error)
    }
  }

  function showCheckoutMixAlert(button) {
    toggleCartAlert(true)

    const cartAlert = document.querySelector('#cart-mix-guard-alert, [data-cart-mix-alert]')
    if (cartAlert) return

    const ctas = button?.closest('.cart__ctas')
    if (ctas) {
      showInlineAlert(ctas, button, CART_MIX_MESSAGE_HTML)
      ctas.querySelector('[data-cart-mix-inline-alert]')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }

  function getIncomingGroup(form) {
    const groupInput = form.querySelector('input[name="properties[_cart_group]"]')
    if (groupInput?.value) return groupInput.value.trim()

    return (form.dataset.cartGroup || '').trim()
  }

  function getCheckoutForm(button) {
    const formId = button.getAttribute('form')
    if (formId) return document.getElementById(formId)
    return button.form || null
  }

  async function handleAddToCartSubmit(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    if (event.defaultPrevented) return

    const incomingGroup = getIncomingGroup(form)
    if (!incomingGroup) return

    event.preventDefault()

    try {
      const cart = await fetchCart()

      if (wouldMix(cart, incomingGroup)) {
        applyProductFormStates(cart)
        return
      }

      const formAlert = form.querySelector('[data-cart-mix-inline-alert]')
      if (formAlert) formAlert.hidden = true

      form.submit()
    } catch (error) {
      console.error('cart-mix-guard: failed to validate add to cart', error)
    }
  }

  function bindAddToCartForms() {
    document.querySelectorAll(PRODUCT_FORM_SELECTORS).forEach((form) => {
      if (form.dataset.cartMixGuardBound === 'true') return
      form.dataset.cartMixGuardBound = 'true'
      form.addEventListener('submit', handleAddToCartSubmit)
    })
  }

  function bindCheckoutButtons() {
    CHECKOUT_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((button) => {
        if (button.dataset.cartMixGuardBound === 'true') return
        button.dataset.cartMixGuardBound = 'true'
        button.addEventListener('click', async (event) => {
          if (button.dataset.cartMixGuardBypass === 'true') {
            delete button.dataset.cartMixGuardBypass
            return
          }

          event.preventDefault()

          try {
            const cart = await fetchCart()

            if (isMixedCart(cart)) {
              showCheckoutMixAlert(button)
              return
            }

            if (getCheckoutForm(button)) {
              button.dataset.cartMixGuardBypass = 'true'
              button.click()
            }
          } catch (error) {
            console.error('cart-mix-guard: failed to validate checkout', error)
          }
        })
      })
    })
  }

  function init() {
    markServerDisabledCheckoutButtons()
    markServerDisabledAddButtons()
    bindAddToCartForms()
    bindCheckoutButtons()
    refreshCartMixState()

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        markServerDisabledCheckoutButtons()
        bindAddToCartForms()
        bindCheckoutButtons()

        if (event?.cartData) {
          applyCartMixGuardState(event.cartData)
          return
        }

        refreshCartMixState()
      })

      if (PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          refreshCartMixState()
        })
      }
    }

    document.addEventListener('shopify:section:load', () => {
      markServerDisabledCheckoutButtons()
      markServerDisabledAddButtons()
      bindAddToCartForms()
      bindCheckoutButtons()
      refreshCartMixState()
    })

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshCartMixState()
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
